class CustomSearchPopup extends HTMLElement {
  constructor() {
    super();
    this.body = document.body;
    this.openBtn = this.body.querySelector(".js-search-header--custom");
    this.searchContainer = this.body.querySelector(".site-header__search-container");
    this.searchBackground = this.body.querySelector(".predictive__screen");
    this.backBtn = this.body.querySelector(".btn-back-search--custom");
    this.clearBtn = this.body.querySelector(".btn-close-search--custom");
    this.input = this.body.querySelector('#Search');
    this.resultsHeading = this.querySelector(".search-content__products .heading");
    this.originalResultsHeading = this.resultsHeading?.textContent || '';
    this.resultsContainer = this.querySelector('#SearchResults');
    this.originalResultsHTML = this.resultsContainer?.innerHTML || '';
    this.viewAllContainer = this.querySelector('#ViewAllContainer');
    this.debouncedHandleSearchInput = this.debounce(this.handleSearchInput.bind(this), 500);

    this.storefrontToken = '1b6fb80e9d47a09bf6147e9744d97c2b';
    this.discountCache = new Map();
    this.currentRequestId = 0;
  }

  connectedCallback() {
    this.setupEventListeners();
    this.initSwiper();
    this.removeRedundantTitles();
  }

  removeRedundantTitles() {
    // Remove title attributes that duplicate the link text for accessibility
    const links = this.querySelectorAll('a[title]');
    links.forEach(link => {
      const title = link.getAttribute('title');
      const linkText = link.textContent.trim();
      // Remove title if it exactly matches the visible link text
      if (title === linkText) {
        link.removeAttribute('title');
      }
    });
  }

  setupEventListeners() {
    this.clearBtn.addEventListener("click", this.clearSearchResults.bind(this));
    this.input.addEventListener('input', this.debouncedHandleSearchInput);

    this.handleKeydown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeSearch();
      }
    };

    this.openBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.openSearch();
    });

    this.backBtn.addEventListener("click", () => this.closeSearch());
    this.searchBackground.addEventListener("click", () => this.closeSearch());
  }

  openSearch() {
    this.previouslyFocused = document.activeElement;
    this.searchContainer.appendChild(this);
    this.body.appendChild(this.searchContainer);
    this.body.classList.add('no-scroll');
    this.classList.remove('hide');
    this.searchBackground.classList.add("is-active");
    this.searchContainer.classList.add("is-active");
    document.addEventListener('keydown', this.handleKeydown);
    setTimeout(() => this.input.focus(), 50);
  }

  closeSearch() {
    this.clearSearchResults();
    this.body.classList.remove('no-scroll');
    this.searchContainer.classList.remove("is-active");
    this.classList.add("hide");
    this.searchBackground.classList.remove("is-active");
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus();
    }
  }

  clearSearchResults() {
    this.input.value = '';
    this.clearBtn.classList.add('hide');
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = this.originalResultsHTML;
      this.resultsContainer.classList.remove('search-results');
    }
    if (this.resultsHeading) this.resultsHeading.textContent = this.originalResultsHeading;
    if (this.viewAllContainer) this.viewAllContainer.innerHTML = '';
  }

  debounce(fn, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  selectBestDiscount({ productPercent, productAmount, collectionPercents }) {
    const productPercentValue = this.parseNumericValue(productPercent);
    const productAmountValue = this.parseNumericValue(productAmount);
    const collectionPercentValues = (collectionPercents || [])
      .map(percentValue => this.parseNumericValue(percentValue))
      .filter(percentValue => percentValue !== null);

    if (productPercentValue !== null) {
      return { type: "PERCENT", value: productPercentValue };
    }
    if (productAmountValue !== null) {
      return { type: "AMOUNT", value: productAmountValue };
    }
    if (collectionPercentValues.length) {
      const maxCollectionPercent = Math.max(...collectionPercentValues);
      return { type: "PERCENT", value: maxCollectionPercent };
    }
    return null;
  }

  parseNumericValue(value) {
    if (value == null || value === '' || value === '-') return null;
    const num = Number(value);
    return num > 0 ? num : null;
  }

  isComingSoon(product) {
    return Array.isArray(product?.tags) && product.tags.includes('coming_soon');
  }

  applyDiscount(originalPrice, discount) {
    if (!discount) {
      return originalPrice;
    }

    let finalPrice = originalPrice;
    if (discount.type === "PERCENT") {
      finalPrice = originalPrice * (1 - discount.value / 100);
    }
    if (discount.type === "AMOUNT") {
      finalPrice = originalPrice - discount.value;
    }

    finalPrice = Math.max(0, finalPrice);
    return Math.round(finalPrice * 100) / 100;
  }

  async fetchDiscountsForHandles(handles) {
    if (!this.storefrontToken) {
      for (const handle of handles) {
        if (handle && !this.discountCache.has(handle)) {
          this.discountCache.set(handle, null);
        }
      }
      return;
    }

    const uncachedHandles = handles.filter(handle => handle && !this.discountCache.has(handle));
    if (!uncachedHandles.length) {
      return;
    }

    const batchSize = 10;
    const productsData = [];
    
    for (let i = 0; i < uncachedHandles.length; i += batchSize) {
      const handleBatch = uncachedHandles.slice(i, i + batchSize);
      
      const productQueries = handleBatch.map((handle, index) => {
        const queryAlias = `product${index}`;
        return `
          ${queryAlias}: product(handle: "${handle}") {
            handle
            productPercent: metafield(namespace: "custom", key: "off") { value }
            productAmount: metafield(namespace: "custom", key: "amount_off") { value }
            collections(first: 50) {
              nodes {
                collectionPercent: metafield(namespace: "custom", key: "off") { value }
              }
            }
          }
        `;
      }).join('\n');

      const graphqlQuery = `
        query ProductsByHandles {
          ${productQueries}
        }
      `;

      try {
        const response = await fetch("/api/2024-10/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": this.storefrontToken,
          },
          body: JSON.stringify({ query: graphqlQuery }),
        });

        const responseData = await response.json();
        
        const productsFromBatch = handleBatch.map((handle, index) => {
          const queryAlias = `product${index}`;
          return responseData?.data?.[queryAlias] || null;
        }).filter(Boolean);
        
        productsData.push(...productsFromBatch);
      } catch (error) {
      }
    }

    for (const productData of productsData) {
      const productPercent = productData.productPercent?.value;
      const productAmount = productData.productAmount?.value;

      const collectionPercents = (productData.collections?.nodes ?? [])
        .map(collection => collection.collectionPercent?.value)
        .filter(percentValue => percentValue !== null && percentValue !== undefined);

      const discount = this.selectBestDiscount({ productPercent, productAmount, collectionPercents });
      this.discountCache.set(productData.handle, discount);
    }

    for (const handle of uncachedHandles) {
      if (!this.discountCache.has(handle)) {
        this.discountCache.set(handle, null);
      }
    }
  }

  renderResults(results, discountsMap) {
    const seriesKeywords = ['series', 'edition', 'collection', 'dalstrong'];

    this.resultsContainer.classList.add('search-results');
    this.resultsContainer.innerHTML = results.map(product => {
      if (product.handle?.includes("mystery-gift")) return '';

      const titleParts = (product.title || '').split('|').map(part => part.trim());
      let seriesPartIndex = 1;
      for (let i = 0; i < titleParts.length; i++) {
        const lowercasePart = (titleParts[i] || '').toLowerCase();
        if (seriesKeywords.some(keyword => lowercasePart.includes(keyword))) {
          seriesPartIndex = i;
          break;
        }
      }

      const series = titleParts[seriesPartIndex] || '';
      const mainTitle = titleParts[0] || '';

      const discount = discountsMap.get(product.handle) || null;
      const currency = window.Shopify?.currency?.active || 'USD';
      
      const currentPrice = Number(product.price);
      const compareAtPrice = product.compare_at_price_min ? Number(product.compare_at_price_min) : null;
      
      const hasDiscount = !!discount;
      
      const finalPrice = hasDiscount 
        ? this.applyDiscount(currentPrice, discount) 
        : currentPrice;

      const priceToCompare = hasDiscount
        ? (compareAtPrice || currentPrice)
        : (compareAtPrice && compareAtPrice > currentPrice ? compareAtPrice : null);

      const shouldShowComparePrice = priceToCompare && priceToCompare > finalPrice;
      const comparePriceToDisplay = shouldShowComparePrice ? priceToCompare : (hasDiscount ? currentPrice : null);

      return `
        <div class="predictive-result">
          <a href="${product.url}">
            <img src="${product.featured_image?.url || ''}" alt="${product.title}" width="40">
            <div class="search-content__product-content-container">
              <p class="search-content__product-series">${series}</p>
              <p class="search-content__product-title">${mainTitle}</p>
              <div class="search-content__product-price-container">
                <p data-product-id="${product.id}" class="igPrice search-content__product-price${hasDiscount ? ' discounted' : ''}">${finalPrice.toLocaleString('en-US', { style: 'currency', currency: currency })}</p>
                ${comparePriceToDisplay ? `<p class="search-content__product-price--compare">${comparePriceToDisplay.toLocaleString('en-US', { style: 'currency', currency: currency })}</p>` : ''}
              </div>
            </div>
          </a>
        </div>
      `;
    }).join('');
  }

  async handleSearchInput() {
    const requestId = ++this.currentRequestId;
    const searchQuery = this.input.value.trim();

    if (searchQuery.length === 0) {
      this.clearSearchResults();
      return;
    }

    this.clearBtn.classList.remove('hide');

    if (searchQuery.length < 2) {
      this.resultsContainer.innerHTML = '';
      this.viewAllContainer.innerHTML = '';
      return;
    }

    try {
      const suggestRequest = fetch(`/search/suggest.json?q=${encodeURIComponent(searchQuery)}&resources[type]=product`);
      const fullSearchRequest = fetch(`/search?q=${encodeURIComponent(searchQuery)}&type=product`);

      const suggestResponse = await suggestRequest;
      const suggestData = await suggestResponse.json();
      const searchResults = (suggestData?.resources?.results?.products || [])
        .filter(product => !this.isComingSoon(product));

      if (requestId !== this.currentRequestId) return;

      const productHandles = searchResults.map(product => product.handle).filter(Boolean);
      await this.fetchDiscountsForHandles(productHandles);

      if (requestId !== this.currentRequestId) return;

      this.renderResults(searchResults, this.discountCache);

      this.resultsHeading.innerHTML = `<div class="loader"></div>`;
      this.viewAllContainer.innerHTML = '';

      const fullSearchResponse = await fullSearchRequest;
      const searchHtml = await fullSearchResponse.text();

      if (requestId !== this.currentRequestId) return;

      const parser = new DOMParser();
      const searchDocument = parser.parseFromString(searchHtml, 'text/html');
      const resultsCountElement = searchDocument.querySelector('#SearchResultsCount');

      if (resultsCountElement) {
        const countMatch = resultsCountElement.textContent.match(/(\d+)/);
        const resultsCount = countMatch ? countMatch[1] : null;

        if (resultsCount) {
          this.resultsHeading.textContent = `${resultsCount} results`;
          this.viewAllContainer.innerHTML = `
            <a href="/search?q=${encodeURIComponent(searchQuery)}&type=product" class="predictive-view-all__link">
              View all
            </a>
          `;
        } else {
          this.viewAllContainer.innerHTML = '';
        }
      } else {
        this.viewAllContainer.innerHTML = '';
      }

    } catch (error) {
      this.resultsContainer.innerHTML = '';
      this.viewAllContainer.innerHTML = '';
    }
  }

  initSwiper() {
    const swiperElement = this.querySelector('.search-content__content .swiper');
    if (swiperElement) {
      new Swiper(swiperElement, {
        slidesPerView: 1,
        loop: true,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev'
        }
      });
    }
  }
}

customElements.define('custom-search-popup', CustomSearchPopup);